<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WhatsappImportAPI from 'dashboard/api/whatsappImport';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationId: { type: [Number, String], required: true },
});

const emit = defineEmits(['cancel', 'update:show']);

const { t } = useI18n();

const file = ref(null);
const senders = ref([]);
const senderMap = ref({});
const step = ref('upload'); // 'upload' | 'map' | 'importing'
const importing = ref(false);
const parsing = ref(false);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const canImport = computed(() => {
  return (
    senders.value.length > 0 &&
    senders.value.every(s => senderMap.value[s])
  );
});

function onFileChange(event) {
  const selected = event.target.files[0];
  if (selected) {
    file.value = selected;
  }
}

async function parseFile() {
  if (!file.value) return;
  parsing.value = true;
  try {
    const { data } = await WhatsappImportAPI.parse(
      props.conversationId,
      file.value
    );
    senders.value = data.senders;
    senderMap.value = {};
    data.senders.forEach(s => {
      senderMap.value[s] = '';
    });
    step.value = 'map';
  } catch {
    useAlert(t('CONTACT_PANEL.IMPORT_WHATSAPP_ERROR'));
  } finally {
    parsing.value = false;
  }
}

async function startImport() {
  importing.value = true;
  step.value = 'importing';
  try {
    const { data } = await WhatsappImportAPI.import(
      props.conversationId,
      file.value,
      senderMap.value
    );
    useAlert(
      t('CONTACT_PANEL.IMPORT_WHATSAPP_SUCCESS', {
        count: data.count,
      })
    );
    emit('cancel');
  } catch {
    useAlert(t('CONTACT_PANEL.IMPORT_WHATSAPP_ERROR'));
    step.value = 'map';
  } finally {
    importing.value = false;
  }
}

function onCancel() {
  emit('cancel');
}
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onCancel">
    <div class="flex flex-col h-auto overflow-auto p-6">
      <woot-modal-header
        :header-title="$t('CONTACT_PANEL.IMPORT_WHATSAPP')"
      />

      <!-- Step 1: Upload -->
      <div v-if="step === 'upload'" class="flex flex-col gap-4">
        <p class="text-sm text-n-slate-11">
          {{ $t('WHATSAPP_IMPORT.UPLOAD_DESC') }}
        </p>
        <input
          type="file"
          accept=".zip"
          class="text-sm"
          @change="onFileChange"
        />
        <div class="flex justify-end gap-2">
          <NextButton
            faded
            slate
            :label="$t('WHATSAPP_IMPORT.CANCEL')"
            @click="onCancel"
          />
          <NextButton
            :label="$t('WHATSAPP_IMPORT.NEXT')"
            :disabled="!file || parsing"
            :is-loading="parsing"
            @click="parseFile"
          />
        </div>
      </div>

      <!-- Step 2: Map senders -->
      <div v-if="step === 'map'" class="flex flex-col gap-4">
        <p class="text-sm text-n-slate-11">
          {{ $t('WHATSAPP_IMPORT.MAP_DESC') }}
        </p>
        <div
          v-for="sender in senders"
          :key="sender"
          class="flex items-center gap-3"
        >
          <span class="text-sm font-medium min-w-[120px]">{{ sender }}</span>
          <select
            v-model="senderMap[sender]"
            class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background"
          >
            <option value="">
              {{ $t('WHATSAPP_IMPORT.SELECT_TYPE') }}
            </option>
            <option value="incoming">
              {{ $t('WHATSAPP_IMPORT.CONTACT') }}
            </option>
            <option value="outgoing">
              {{ $t('WHATSAPP_IMPORT.AGENT') }}
            </option>
          </select>
        </div>
        <div class="flex justify-end gap-2">
          <NextButton
            faded
            slate
            :label="$t('WHATSAPP_IMPORT.CANCEL')"
            @click="onCancel"
          />
          <NextButton
            :label="$t('WHATSAPP_IMPORT.IMPORT')"
            :disabled="!canImport"
            @click="startImport"
          />
        </div>
      </div>

      <!-- Step 3: Importing -->
      <div
        v-if="step === 'importing'"
        class="flex flex-col items-center gap-4 py-8"
      >
        <span
          class="i-lucide-loader-2 text-2xl animate-spin text-n-blue-10"
        />
        <p class="text-sm text-n-slate-11">
          {{ $t('WHATSAPP_IMPORT.IMPORTING') }}
        </p>
      </div>
    </div>
  </woot-modal>
</template>
